import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { useNicknames } from '../lib/useNicknames.js';

interface RefundProduct {
  sku: string; asin: string; title: string;
  unitsSold: number; unitsReturned: number; refundRate: number;
}

const fmtNum = (n: number) => new Intl.NumberFormat('en-US').format(n);

function rateColor(rate: number): string {
  if (rate <= 0) return '';
  if (rate < 3) return 'bg-green-50 text-green-800';
  if (rate < 8) return 'bg-yellow-50 text-yellow-800';
  return 'bg-red-50 text-red-800';
}

function rateBadge(rate: number): string {
  if (rate < 3) return 'Good';
  if (rate < 8) return 'Watch';
  return 'High';
}

export function SalesRefundRate() {
  const [days, setDays] = useState(90);
  const [data, setData] = useState<RefundProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const { displayName, parentNickname } = useNicknames();

  useEffect(() => {
    setLoading(true);
    const end = new Date().toISOString().slice(0, 10);
    const start = new Date(Date.now() - days * 86400000).toISOString().slice(0, 10);
    api.get<{ products: RefundProduct[] }>(`/sales/refund-rate?start=${start}&end=${end}`)
      .then((r) => setData(r.products))
      .finally(() => setLoading(false));
  }, [days]);

  const totalSold = data.reduce((s, p) => s + p.unitsSold, 0);
  const totalReturned = data.reduce((s, p) => s + p.unitsReturned, 0);
  const overallRate = totalSold > 0 ? (totalReturned / totalSold) * 100 : 0;

  return (
    <div className="p-6 max-w-5xl">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold">Refund Rate</h1>
        <p className="text-xs text-gray-400">Refund quantity based on units sold during the selected period</p>
      </div>

      {/* Period selector */}
      <div className="flex gap-1 mb-4">
        {[30, 60, 90, 180, 365].map((d) => (
          <button key={d} onClick={() => setDays(d)}
            className={`px-3 py-1 text-sm rounded ${days === d ? 'bg-blue-600 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}>
            {d}d
          </button>
        ))}
      </div>

      {loading && <p className="text-gray-400 text-sm">Loading...</p>}

      {/* KPI cards */}
      {!loading && data.length > 0 && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="border rounded-xl p-4">
            <p className="text-xs text-gray-500">Units Sold</p>
            <p className="text-2xl font-bold">{fmtNum(totalSold)}</p>
          </div>
          <div className="border rounded-xl p-4">
            <p className="text-xs text-gray-500">Units Returned</p>
            <p className="text-2xl font-bold">{fmtNum(totalReturned)}</p>
          </div>
          <div className={`border rounded-xl p-4 ${rateColor(overallRate)}`}>
            <p className="text-xs text-gray-500">Overall Refund Rate</p>
            <p className="text-2xl font-bold">{overallRate.toFixed(1)}%</p>
          </div>
        </div>
      )}

      {/* Product table */}
      {data.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b text-xs">
                <th className="text-left px-4 py-2">Product</th>
                <th className="text-left px-3 py-2">ASIN</th>
                <th className="text-right px-3 py-2">Units Sold</th>
                <th className="text-right px-3 py-2">Returned</th>
                <th className="text-right px-3 py-2">Refund Rate</th>
                <th className="text-center px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.map((p) => (
                <tr key={p.sku} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-2" title={p.sku}>
                    {parentNickname(p.sku) && <div className="text-[10px] text-gray-400">{parentNickname(p.sku)}</div>}
                    <div className="font-medium text-sm">{displayName(p.sku) || p.title?.slice(0, 40) || p.sku}</div>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600 font-mono">{p.asin}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtNum(p.unitsSold)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtNum(p.unitsReturned)}</td>
                  <td className={`px-3 py-2 text-right tabular-nums font-medium ${rateColor(p.refundRate)}`}>
                    {p.refundRate.toFixed(1)}%
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span className={`inline-block px-2 py-0.5 text-xs rounded-full ${
                      p.refundRate < 3 ? 'bg-green-100 text-green-700'
                        : p.refundRate < 8 ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {rateBadge(p.refundRate)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
